import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  Chip,
  Paper,
  Button,
  Tooltip,
  Badge,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment
} from '@mui/material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { StoredAnalysis } from '../types/analysis';
import { getStoredAnalyses, getStoredAnalysis } from '../services/storage';
import AnalysisResults from '../components/AnalysisResults';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Add color palette values needed for styling
const colors = {
  yellowSea: '#f9ad03',
};

const MotionPaper = motion(Paper);

export default function History() {
  const [historyItems, setHistoryItems] = useState<StoredAnalysis[]>([]);
  const [filteredItems, setFilteredItems] = useState<StoredAnalysis[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedAnalysis, setSelectedAnalysis] = useState<StoredAnalysis | null>(null);
  const [, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadHistoryItems();
  }, [page, rowsPerPage]);

  useEffect(() => {
    filterItems();
  }, [searchTerm, historyItems]);

  const loadHistoryItems = async () => {
    setLoading(true);
    try {
      const response = await getStoredAnalyses(page + 1, rowsPerPage);
      console.log('Loaded history items:', response.items);
      setHistoryItems(response.items);
      setFilteredItems(response.items);
      setTotalItems(response.total);
      setError(null);
    } catch (error) {
      console.error('Error loading history:', error);
      setError('Failed to load history. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    if (!searchTerm.trim()) {
      setFilteredItems(historyItems);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = historyItems.filter(item => 
      item.url.toLowerCase().includes(lowerSearchTerm) || 
      (item.businessDetails?.name || item.summary?.businessName || '').toLowerCase().includes(lowerSearchTerm) ||
      item.requestId.toLowerCase().includes(lowerSearchTerm)
    );
    
    setFilteredItems(filtered);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    loadHistoryItems();
    setSearchTerm('');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSelectItem = async (item: StoredAnalysis) => {
    // Save the current scroll position before switching views
    setScrollPosition(window.scrollY);
    console.log('Selected item:', item);
    
    try {
      setLoading(true);
      // For detailed view, we need to fetch the full analysis data
      const fullAnalysis = await getStoredAnalysis(item.requestId);
      console.log('Full analysis loaded:', fullAnalysis);
      
      if (fullAnalysis) {
        // Use the full analysis data directly - it already has the correct structure
        setSelectedAnalysis(fullAnalysis);
      } else {
        // If full analysis fetch fails, use the list item data
        console.log('Using list item as fallback:', item);
        setSelectedAnalysis(item);
      }
    } catch (error) {
      console.error('Error loading analysis details:', error);
      setError('Failed to load analysis details. Please try again.');
      // If there's an error, still show the list item data
      setSelectedAnalysis(item);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedAnalysis(null);
    
    // Restore scroll position after a small delay
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 100);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            mb: 4
          }}>
            {selectedAnalysis ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                  sx={{ 
                    mb: { xs: 2, sm: 0 },
                    borderColor: 'rgba(255, 63, 122, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: 'rgba(255, 63, 122, 0.1)',
                    }
                  }}
                >
                  Back to History
                </Button>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    color: 'white'
                  }}
                >
                  {selectedAnalysis.businessDetails?.name || selectedAnalysis.summary?.businessName || 'Analysis Results'}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    mb: { xs: 2, sm: 0 },
                    color: 'white'
                  }}
                >
                  Analysis History
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                    size="small"
                    sx={{ 
                      height: 40,
                      borderColor: 'rgba(255, 63, 122, 0.5)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: 'rgba(255, 63, 122, 0.1)',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!selectedAnalysis && (
            <MotionPaper
              elevation={2}
              sx={{ 
                borderRadius: 2, 
                overflow: 'hidden',
                height: '100%',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(25, 30, 50, 0.5)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Box sx={{ 
                p: 2, 
                background: theme.palette.primary.main, 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    History
                  </Typography>
                  <Badge 
                    badgeContent={filteredItems.length} 
                    color="warning" 
                    sx={{ 
                      ml: 1,
                      '& .MuiBadge-badge': {
                        backgroundColor: colors.yellowSea,
                        color: '#000000',
                        fontWeight: 'bold'
                      }
                    }}
                    max={999}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search history..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.4)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.6)',
                        },
                      }
                    }}
                  />
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Business</TableCell>
                      <TableCell>URL</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((item) => {
                      console.log('Rendering row item:', {
                        requestId: item.requestId,
                        url: item.url,
                        businessName: item.businessDetails?.name || item.summary?.businessName,
                        analysisTime: item.analysisTime
                      });
                      
                      // Business name can be in businessDetails.name or summary.businessName
                      const businessName = item.businessDetails?.name || item.summary?.businessName || 'Unknown Business';
                      
                      // Duration is in analysisTime
                      const duration = item.analysisTime ? 
                                      `${(item.analysisTime / 1000).toFixed(1)}s` : 
                                      'N/A';
                      
                      return (
                        <TableRow 
                          key={item.requestId} 
                          hover 
                          onClick={() => handleSelectItem(item)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)'
                            }
                          }}
                        >
                          <TableCell>
                            {businessName}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Link 
                                href={item.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ 
                                  color: 'white',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {item.url}
                              </Link>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(item.url, '_blank');
                                }}
                                sx={{ 
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                  }
                                }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {format(new Date(item.storedAt), 'MMM d, yyyy HH:mm')}
                          </TableCell>
                          <TableCell>
                            {duration}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={item.status || 'Unknown'} 
                              size="small"
                              sx={{ 
                                backgroundColor: item.status === 'success' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                color: item.status === 'success' ? '#4caf50' : '#f44336'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Analysis">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectItem(item);
                                  }}
                                  sx={{ color: 'white' }}
                                >
                                  <OpenInNewIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalItems > 0 && (
                <TablePagination
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{
                    color: 'white',
                    '& .MuiSvgIcon-root': {
                      color: 'white'
                    },
                    '& .MuiTablePagination-select': {
                      color: 'white'
                    },
                    '& .MuiTablePagination-selectIcon': {
                      color: 'white'
                    },
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    backgroundColor: 'rgba(18, 21, 40, 0.5)'
                  }}
                />
              )}
            </MotionPaper>
          )}

          {selectedAnalysis && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <AnalysisResults result={selectedAnalysis} />
            </motion.div>
          )}
        </motion.div>
      </Box>
    </Container>
  );
}